import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Grid } from '@mui/material';
import type { Nullable } from '@provider-portal/common/types/types';
import RequestStateView from '@provider-portal/menu-common/components/RequestStateView/RequestStateView';
import { push } from 'connected-react-router';

import { selectSelectedProvider } from 'rdx/account';
import {
  getMenuAsyncAction,
  selectMenu,
  selectMenuRequestState,
  selectVisibleMenuItemIds,
} from 'rdx/menu';
import { selectQuery } from 'rdx/router';
import { LocaleType } from 'utils/translations';

import MenuCategory from './MenuCategory';
import MenuContent from './MenuContent';
import MenuLanguage from './MenuLanguage';
import { MenuSideBarSection } from './MenuSideBarSection';

function useActions() {
  const dispatch = useDispatch();

  return useMemo(
    () => ({
      getMenu: (locale: LocaleType) => dispatch(getMenuAsyncAction.request({ locale })),
      selectCategory: (categoryId: number) =>
        dispatch(push({ search: `?selectedCategoryId=${categoryId}` })),
    }),
    [dispatch],
  );
}

function useProps() {
  const actions = useActions();
  const menu = useSelector(selectMenu);
  const menuItemIds = useSelector(selectVisibleMenuItemIds);
  const requestState = useSelector(selectMenuRequestState);
  const { selectedCategoryId } = useSelector(selectQuery);
  const selectedProvider = useSelector(selectSelectedProvider);
  const locales = (selectedProvider && selectedProvider.city_locales) || [LocaleType.English];

  return {
    menu,
    actions,
    menuItemIds,
    requestState,
    selectedCategoryId,
    locales,
  };
}

interface MenuEditorProps extends ReturnType<typeof useProps> {}

const MenuEditor: React.FC<MenuEditorProps> = React.memo(
  ({ menu, actions, selectedCategoryId, requestState, menuItemIds, locales }) => {
    const [menuLocale, setMenuLocale] = useState<Nullable<LocaleType>>(null);

    const firstCategoryId = menu.result.categories[0];

    useEffect(() => {
      // small trick to have default selected category in url
      if (firstCategoryId && !selectedCategoryId) {
        actions.selectCategory(firstCategoryId);
      }
    }, [actions, firstCategoryId, selectedCategoryId]);

    useEffect(() => {
      // getMenu on menuLocale change
      if (menuLocale) {
        actions.getMenu(menuLocale);
      }
    }, [actions, menuLocale, locales]);

    useEffect(() => {
      // if menuLocale is not set, or does not belong to locales, reset it to first one from locales
      if (locales.length && (!menuLocale || !locales.includes(menuLocale))) {
        setMenuLocale(locales[0]);
      }
    }, [menuLocale, locales]);

    return (
      <RequestStateView
        isEmpty={false}
        requestState={requestState}
        intlIdEmpty="widgets.MenuEditor.noData"
        intlIdError="widgets.MenuEditor.error"
      >
        <Grid container columnSpacing={2} rowSpacing={2}>
          <Grid item xl={3} xs={12}>
            <Box>
              <MenuSideBarSection titleIntl="widgets.MenuEditor.locale">
                {locales.map((lang) => (
                  <MenuLanguage
                    key={lang}
                    locale={lang}
                    isActive={lang === menuLocale}
                    onSelect={setMenuLocale}
                  />
                ))}
              </MenuSideBarSection>
            </Box>
            <Box mt={2}>
              <MenuSideBarSection titleIntl="widgets.MenuEditor.categories">
                {menu.result.categories.map((categoryId) => (
                  <MenuCategory key={categoryId} id={categoryId} />
                ))}
              </MenuSideBarSection>
            </Box>
            <Box mt={2}>
              <MenuSideBarSection titleIntl="widgets.MenuEditor.options">
                <MenuCategory id="options" />
              </MenuSideBarSection>
            </Box>
          </Grid>
          <Grid item xl={9} xs={12}>
            <MenuContent itemIds={menuItemIds} requestState={requestState} />
          </Grid>
        </Grid>
      </RequestStateView>
    );
  },
);

export default () => <MenuEditor {...useProps()} />;
